import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useLocation, useNavigate } from 'react-router-dom';

import { PriceChange } from 'pages/trading/model';
import { calculateDailyStats } from 'pages/trading/price/CalculateDailyStats';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { calcGrowth } from 'utils/calc';
import {
  PromotionTableRow,
  usePromotionTableColumns,
} from 'pages/trading/price/promotion/promotionTableDefinitionHooks';

interface Props {
  data: PriceChange[];
  messageId?: number;
}

export function PromotionTable({ data, messageId = undefined }: Props) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const columns = usePromotionTableColumns();

  const rows = useMemo(() => {
    return data
      .map(styleRow => {
        const oldStats = calculateDailyStats(styleRow.fromPrice);
        const promoStats = calculateDailyStats(styleRow.promotionPrice);
        const newStats = calculateDailyStats(styleRow.toPrice);

        const styleParts = styleRow.styNumber?.split('|') || [];
        const variantColor = styleParts[1] || intl.formatMessage({ id: 'trading.price_change.table.style' });

        const rowPriceChangeDelta = calcGrowth(newStats.price, oldStats.price);

        return {
          id: styleRow.styNumber || `${styleRow.prdNumber}-${styleRow.chnName}`,
          artImageUrl: 'imageUrl' in styleRow ? ((styleRow as { imageUrl?: string }).imageUrl ?? '') : '',
          prdName: styleRow.prdName || styleRow.prdNumber,
          styBrandColor: variantColor,
          chnName: styleRow.chnName || '',
          oldPrice: oldStats.price,
          newPrice: newStats.price,
          priceChangeDelta: rowPriceChangeDelta,
          promoPrice: promoStats ? promoStats.price : undefined,
          dailyGmv: newStats.dailyGmv,
          dailyGmvDelta: calcGrowth(newStats.dailyGmv, oldStats.dailyGmv),
          dailyQty: newStats.dailyQty,
          oldDailyQty: oldStats.dailyQty,
          dailyQtyDelta: calcGrowth(newStats.dailyQty, oldStats.dailyQty),
          originalData: styleRow,
        };
      })
      .sort((a, b) => b.dailyGmv - a.dailyGmv);
  }, [data, intl]);

  const onRowClick = (row: Row<unknown>) => {
    const castedRow = row.original as PromotionTableRow;
    if (!castedRow.originalData) return;

    const styleRow = castedRow.originalData;
    const styleUniqueId = styleRow.styNumber || styleRow.prdNumber;
    const targetId = encodeURIComponent(`${styleUniqueId}::${styleRow.chnName || ''}`);

    const currentSearchParams = new URLSearchParams(location.search);
    currentSearchParams.set('isPromotion', 'true');

    navigate({
      pathname: `/steeringhub/insightsfeed/price-analysis/${messageId}/style/${targetId}`,
      search: currentSearchParams.toString(),
    });
  };

  return (
    <div className="mb-0 px-0 bg-white rounded-2xl overflow-hidden">
      <DataTableContextProvider>
        <DataTable
          titleId={intl.formatMessage({
            id: 'trading.price_change.breakdown.title',
          })}
          columns={columns as ColumnDef<unknown>[]}
          rows={rows}
          onRowClick={messageId ? onRowClick : undefined}
          exportName={`price-analysis-breakdown-${messageId}`}
        />
      </DataTableContextProvider>
    </div>
  );
}
